import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Loader } from "semantic-ui-react";
import Typewriter from "typewriter-effect";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { SG_GET_CHAT_GPT_SUMMARY } from "constants/actions";
import { v4 as uuidv4 } from "uuid";
import SkipFollowupQuestionModal from "../components/SkipFollowupQuestionModal";
import { followupAiTypeQuestion } from "../constants/questionTypes";

const NewValidationToken = uuidv4();

const AIFollowUpQuestion = ({
  currentQuestionDet,
  saveUpdatedAnswers,
  refQues,
  handleNext,
}) => {
  const dispatch = useDispatch();
  const timeout = useRef(null);

  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [open, setOpen] = useState(false);

  const textareaRef = useRef();

  // textarea resize on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "inherit";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight - 16}px`;
    }
  }, []);

  const handleInput = (e) => {
    e.target.style.height = "inherit";
    e.target.style.height = `${e.target.scrollHeight - 16}px`;
  };

  const handleChange = (event) => {
    setFeedbackText(event.target.value);
    // Preserve metadata from the reference question (factor, factorTitle, etc.)
    saveUpdatedAnswers(
      currentQuestionDet.id, 
      { 
        ...refQues?.response,  // Include all metadata from the original question
        feedback: event.target.value, 
        question_text: question 
      }, 
      followupAiTypeQuestion
    );
  };

  const { get_chat_gpt } = useSelector(
    (state) => ({
      get_chat_gpt: state.chatgpt,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (
      //      !currentQuestionDet?.response?.feedback ||
      //      currentQuestionDet?.response?.prompt_feedback !== refQues?.response?.feedback

      !currentQuestionDet?.response?.question_text


    ) {
      SubmitChatGpt();
    } else {
      setQuestion(currentQuestionDet?.response?.question_text ?? "");
      const initial = currentQuestionDet?.response?.feedback ?? "";
      setFeedbackText(initial);
      // resize after hydration
      if (textareaRef.current) {
        textareaRef.current.style.height = "inherit";
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight - 16}px`;
      }
    }
  }, []);

  useEffect(() => {
    if (get_chat_gpt?.response_pending === true) {
      timeout.current = setTimeout(() => {
        setLoading(false);
      }, 15000);
    }

    if (get_chat_gpt?.response_pending === false) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }

    setLoading(get_chat_gpt?.response_pending);

    if (get_chat_gpt?.chat_gpt_summary?.response) {
      setQuestion(get_chat_gpt.chat_gpt_summary.response);
    }

    return () => {
      clearTimeout(timeout.current);
    };
  }, [get_chat_gpt]);

  function SubmitChatGpt() {
    const _prompt = `Context:
        The following is feedback from a culture analysis survey. The feedback is a response to the following statement: ${refQues?.response?.question} 
        Feedback:
        ${refQues?.response?.feedback}
        Question:
        What follow up question would a world class culture consultant ask based on the feedback given that would help to get more context around this area, and also help to generate recommendations for this organization? Make sure your response is simple enough for a 7th grader to understand. Only return the question itself.
        Response:
        `;
    dispatch({
      type: SG_GET_CHAT_GPT_SUMMARY,
      payload: { payload: _prompt, token: NewValidationToken },
    });
  }

  const handleSkip = () => setOpen(true);

  function SkipQuestion() {
    saveUpdatedAnswers(
      currentQuestionDet.id,
      {
        ...refQues?.response,  // Preserve metadata from the original question
        survey_q_id: currentQuestionDet.id,
        feedback: "",
        skip: true,
        question_text: question,
      },
      followupAiTypeQuestion
    );
    setOpen(false);
    handleNext();
  }

  return (
    <FeedbackContainer>
      <Title>Follow up</Title>

      <Description>
        {loading ? (
          <Loader active inline />
        ) : (
          <Typewriter
            onInit={(typewriter) => {
              typewriter.changeDelay(14).typeString(question || "").pauseFor(100).start();
            }}
          />
        )}
      </Description>

      <StyledTextarea
        id={`txt_${currentQuestionDet?.id}`}
        value={feedbackText || ""}
        onChange={handleChange}
        placeholder="Type your answer here"
        ref={textareaRef}
        onInput={handleInput}
      />

      <Skip onClick={handleSkip}>Skip &gt;</Skip>
      <SkipFollowupQuestionModal open={open} setOpen={setOpen} SkipQuestion={SkipQuestion} />
    </FeedbackContainer>
  );
};

export default AIFollowUpQuestion;

const FeedbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 600px;
  width: 100%;
  font-family: "Raleway", sans-serif;
`;

const Description = styled.div`
  color: #2a3039;
  font-family: "Raleway";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  margin-bottom: 34px;
  line-height: 1.6;
`;

const Title = styled.div`
  color: #2d70e2;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 40px;
  display: flex;
`;

const StyledTextarea = styled.textarea`
  display: block;
  width: 100%;
  padding: 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #2a3039;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  resize: none;
  font-family: "Raleway", sans-serif;

  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #2d70e2;
    outline: 0;
  }
`;

const Skip = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  color: #2d70e2;
  margin-top: 40px;
  cursor: pointer;
`;
